(function internalProvider(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.VideoPlayers = global.NewHomeSource.VideoPlayers || {};

  const scriptUrlInput = document.querySelector('[data-zonda-video-player-src]');

  const defaultEvents = {
    onEnded: function onEnded() {},
    onPlaying: function onPlaying() {},
    onPaused: function onPaused() {},
    onLoaded: function onLoaded() {},
    onMilestone: function onMilestone() {},
  };

  /**
   * Ensures the zonda-video-player custom element is defined before use.
   * Uses the RequireJS alias when available, otherwise resolves immediately
   * (custom element already registered by an earlier script tag).
   *
   * @returns {Promise<void>}
   */
  function loadZondaVideoPlayer() {
    return new Promise((resolve, reject) => {
      if (global.customElements && global.customElements.get('zonda-video-player')) {
        resolve();
        return;
      }

      if (!scriptUrlInput || !scriptUrlInput.value) {
        reject();
        return;
      }

      if (global.define) {
        require(
          [scriptUrlInput.value],
          () => { resolve(); }
        );
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = scriptUrlInput.value;
      script.type = 'text/javascript';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  class InternalProvider {
    /**
     * Initializes a new instance of the InternalProvider player handler.
     */
    constructor() {
      this.player = null;
      this.source = null;
      this.eventsHandler = {};
      this.abortController = null;
      this.hasEnded = false;
    }

    /**
     * Stores the video source URL, merges event callbacks/options, and optionally
     * sets the player element directly to avoid relying on ID-based DOM lookup.
     *
     * @param {string} source - HLS or MP4 URL of the video.
     * @param {object} config - optional callbacks and options:
     *   onEnded, onPlaying, onPaused, onLoaded, onMilestone, playOnLoaded, thumbnailUrl.
     *   created by createContainer(). When provided the instance skips DOM lookup entirely.
     */
    initialize(_, config, mediaId, videoUrl, thumbnailUrl) {
      this.source = videoUrl;
      this.thumbnailUrl = thumbnailUrl;
      this.mediaId = mediaId;
      this.hasEnded = false;
      this.eventsHandler = Object.assign({}, defaultEvents, config);
    }

    /**
     * Loads the zonda-video-player custom element script if not already available,
     * then configures the player element, wires vp:* events, and optionally starts playback.
     *
     * @returns {Promise<void>}
     */
    async createVideo() {
      await loadZondaVideoPlayer();

      this.player = document.querySelector(`[data-zonda-element-id="${this.mediaId}"]`);
      if (!this.player) {
        return;
      }

      this.player.setAttribute('video-url', this.source);

      if (this.thumbnailUrl) {
        this.player.setAttribute('thumbnail-url', this.thumbnailUrl);
      }

      this.addEvents();

      if (this.eventsHandler.playOnLoaded) {
        this.player.play();
      }
    }

    /**
     * Triggers the onPlaying event handler.
     */
    playingHandler(event) {
      const eventData = event;
      eventData.videoId = this.source;
      document.body.dispatchEvent(new CustomEvent('videoInteraction', {
        detail: { originator: 'internalProvider', interaction: 'video_play' }
      }));
      this.eventsHandler.onPlaying(eventData);
    }

    /**
     * Triggers the onPaused event handler.
     * Only dispatches videoInteraction if the video has not already ended.
     */
    pausedHandler(event) {
      const eventData = event;
      eventData.videoId = this.source;
      if (!this.hasEnded) {
        document.body.dispatchEvent(new CustomEvent('videoInteraction', {
          detail: { originator: 'internalProvider', interaction: 'video_pause' }
        }));
      }
      this.eventsHandler.onPaused(eventData);
    }

    /**
     * Triggers the onEnded event handler.
     */
    endedHandler(event) {
      const eventData = event;
      eventData.videoId = this.source;
      this.hasEnded = true;
      document.body.dispatchEvent(new CustomEvent('videoInteraction', {
        detail: { originator: 'internalProvider', interaction: 'video_completed' }
      }));
      this.eventsHandler.onEnded(eventData);
    }

    /**
     * Triggers the onMilestone event handler.
     */
    milestoneHandler(event) {
      this.eventsHandler.onMilestone(event.detail);
    }

    /**
     * @private
     * Attaches vp:* listeners to the player element, aborting any prior listeners first.
     * Also dispatches `videoInteraction` events on document.body to match the convention
     * used by other N1 video providers (Vimeo, YouTube).
     */
    addEvents() {
      this.removeEvents();

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.player.addEventListener('vp:play', this.playingHandler.bind(this), { signal });
      this.player.addEventListener('vp:pause', this.pausedHandler.bind(this), { signal });
      this.player.addEventListener('vp:ended', this.endedHandler.bind(this), { signal });
      this.player.addEventListener('vp:milestone', this.milestoneHandler.bind(this), { signal });
    }

    /**
     * Removes all event listeners from the player element.
     */
    removeEvents() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    /**
     * Resumes or starts playback.
     */
    play() {
      if (this.player) {
        this.player.play();
      }
    }

    /**
     * Pauses playback.
     */
    pause() {
      if (this.player) {
        this.player.pause();
      }
    }

    /**
     * Removes all event listeners and destroys the underlying player element.
     */
    dispose() {
      this.removeEvents();

      if (this.player) {
        if (typeof this.player.destroy === 'function') {
          this.player.destroy();
        }
        this.player = null;
      }
    }

    /**
     * Creates a <zonda-video-player> custom element sized to fill its container.
     * The returned element should be inserted into the DOM and then passed to
     * initialize() so the instance holds a direct reference without needing an ID lookup.
     *
     * @returns {HTMLElement}
     */
    static createContainer(_, mediaId) {
      const player = document.createElement('zonda-video-player');
      player.classList.add('nhs-u-width--full', 'nhs-u-height--full');
      player.setAttribute('layout', 'fill-container');
      player.dataset.zondaElementId = mediaId;
      return player;
    }
  }

  global.NewHomeSource.VideoPlayers.InternalProvider = InternalProvider;
}(window));
